// LANȚUL DIN ANTET: breadcrumb și comutator deodată (A3).
//
//   Situație 2 ▾  ⁄  Arhitectură 3 ▾  ⁄  Structură 1 [macro|detaliu] ▾  ⁄  Planificare
//
// Fiecare segment spune DOAR etapa și soluția aleasă (Stefan, 26.09.2026:
// «e suficient să spui situație și amplasarea 2»), pe UN rând: etapa în gri,
// numărul soluției îngroșat — numele e «Situație 2» peste tot, iar aici nu se
// scrie «Situație» de două ori. Numărătorile — câte sunt,
// câte nu trec, câte macină — sunt ale paginii Soluții, nu ale antetului. Ce
// macină se vede ca un fir de lumină care curge sub segment; faza și progresul
// stau în `title`. Clicul pe segment deschide disciplina pe același fir; ▾
// deschide comutatorul. Segmentul fără nimic pe fir spune «nimic pe A1».
// Planificarea (costurile) n-are comutator: urmează structura — și, până la detaliu, fațada —,
// și apar numai când proiectul are costuri.
//
// Corpul NU apare aici (A6).

import { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useT } from "../i18n";
import { potPleca } from "../nescris";
import { useTipProiect } from "../components/rol";
import { useRegistru } from "./Registru";
import { disciplinaFelului, type Disciplina, type SolutieRand } from "./api";
import Comutator from "./Comutator";
import { useFir } from "./useFir";
import {
  DISCIPLINA_PAGINII, caleAceleiasiPagini, caleEtapa, caleSpreDetaliu, caleTintaCosturi, eDeschis,
  elementulRandului, notaRulare, numarRand, numeEtapa, numeRand, tintaCosturi,
} from "./selectie";
import "./nav.css";

const AMONTE: Partial<Record<Disciplina, Disciplina>> = {
  architecture: "site_plan",
  structure: "architecture",
};

export default function Lant() {
  const t = useT();
  const navigate = useNavigate();
  const { registru, projectId } = useRegistru();
  const { ruta, fir } = useFir();
  const [deschis, setDeschis] = useState<{ d: Disciplina; ancora: DOMRect } | null>(null);
  const inchide = useCallback(() => setDeschis(null), []);
  // Proiectul de structură n-are plan de situație: pornește dintr-o
  // arhitectură gata făcută, deci lanțul lui începe cu ea.
  const etape: Disciplina[] = useTipProiect() === "structura"
    ? ["architecture", "structure"] : ["site_plan", "architecture", "structure"];

  if (!registru) return <nav className="lant" aria-label={t("lant.aria")} />;
  const dupaId = (id: string | null) =>
    (id ? registru.solutii.find((s) => s.id === id) : undefined) ?? null;
  const paginaDisc = DISCIPLINA_PAGINII[ruta.pagina] ?? null;

  const segment = (d: Disciplina) => {
    const rand = dupaId(fir[d]);
    const amonteDisc = AMONTE[d];
    const amonte = amonteDisc ? dupaId(fir[amonteDisc]) : null;
    const on = paginaDisc === d;
    const href = caleEtapa(projectId, d, rand?.id ?? null, amonte?.id ?? null,
                           { vedere: d === "structure" && paginaDisc === d ? ruta.vedere : null });
    // Pe o arhitectură IMPORTATĂ schița nu e un pas al omului, ci prima
    // jumătate a Soluției 1, care vine singură după ea: cât una din ele
    // macină, segmentul spune Soluția care se face, nu «0 · schiță».
    const macroInLucru = rand?.faza === "schita" && amonte && !amonte.lineage.site_plan
      ? registru.solutii.find((s) => s.faza === "macro" && eDeschis(s.stare)
          && s.lineage.architecture === amonte.id)
        ?? (eDeschis(rand.stare) ? { numar: 1 } : null)
      : null;
    const valoare = macroInLucru ? String(macroInLucru.numar)
      : rand ? numarRand(rand)
      : amonte ? t("lant.nimic_pe", { solutie: numeRand(amonte) }) : t("lant.nimic");
    // ce macină: firul de sub segment, iar cuvintele (pasul, trecerea) la hover.
    // Firul curge cât disciplina are ORICE rulare deschisă pe proiect — și pe
    // alt rând decât cel de pe fir, și una care n-are încă rând (importul
    // arhitecturii, «Înțelegem arhitectura») —, nu doar pe rândul ales.
    const rulari = [
      ...(rand && eDeschis(rand.stare) ? [rand.rulare] : []),
      ...registru.solutii
        .filter((s) => s.discipline === d && s.id !== rand?.id && eDeschis(s.stare))
        .map((s) => s.rulare),
      ...registru.rulari_fara_solutie.filter((j) => disciplinaFelului(j.kind) === d),
    ];
    const lucreaza = rulari.length > 0;
    const nota = rulari.map(notaRulare).find(Boolean) ?? null;
    const titlu = [rand?.label, nota].filter(Boolean).join(" · ") || undefined;
    return (
      <div key={d} className={`lant-seg${on ? " on" : ""}${lucreaza ? " lucreaza" : ""}`}>
        <Link className="lant-link" to={href} title={titlu}
              onClick={(e) => { if (!potPleca()) e.preventDefault(); }}>
          <span className="lant-eticheta">{numeEtapa(d)}</span>
          <span className={`lant-valoare${rand ? "" : " mut"}`}>{valoare}</span>
        </Link>
        {d === "structure" && rand && <ComutatorFaza rand={rand} />}
        <button type="button" className="lant-caret"
                aria-label={t("lant.alege", { etapa: numeEtapa(d) })}
                aria-expanded={deschis?.d === d}
                onClick={(e) => {
                  const r = (e.currentTarget.parentElement ?? e.currentTarget)
                    .getBoundingClientRect();
                  setDeschis(deschis?.d === d ? null : { d, ancora: r });
                }}>▾</button>
      </div>
    );
  };

  // COSTURILE: numai când proiectul are costuri, și atunci de pe orice ecran —
  // ale firului (detaliul structurii, altfel fațada arhitecturii), iar când
  // firul n-are, cele mai noi din proiect. Doar cuvântul, fără sumă: cifrele
  // sunt ale paginii.
  const tinta = tintaCosturi(registru, fir);
  const costuri = tinta && (
    <div className={`lant-seg lant-costuri${ruta.pagina === "costuri" ? " on" : ""}`}>
      <Link className="lant-link" to={caleTintaCosturi(projectId, tinta)}
            title={tinta.peFir ? undefined : numeRand(tinta.rand)}
            onClick={(e) => { if (!potPleca()) e.preventDefault(); }}>
        <span className="lant-eticheta">{numeEtapa("costuri")}</span>
      </Link>
    </div>
  );

  return (
    <nav className="lant" aria-label={t("lant.aria")}>
      {etape.map((d, i) => (
        <span key={d} className="lant-pas">
          {i > 0 && <span className="lant-sep" aria-hidden="true" />}
          {segment(d)}
        </span>
      ))}
      {costuri && <span className="lant-sep" aria-hidden="true" />}
      {costuri}
      {deschis && (
        <Comutator disciplina={deschis.d} ancora={deschis.ancora}
                   laInchide={() => { inchide(); }} />
      )}
      {/* Anunțul de la final de fază: stă sub lanț, cu «Deschide» — pe
          detaliul Soluției, dacă s-a terminat de când a apărut anunțul. */}
      <Anunturi onDeschide={(href) => {
        if (potPleca()) navigate(caleSpreDetaliu(registru.solutii, href));
      }} />
    </nav>
  );
}

/** «macro | detaliu»: pe o Soluție N care are amândouă rândurile, comutarea
 *  navighează pe id-ul celuilalt rând (contract §8), pe pagina pe care ești —
 *  vizualizatorul, verificările sau costurile. */
function ComutatorFaza({ rand }: { rand: SolutieRand }) {
  const t = useT();
  const navigate = useNavigate();
  const { registru, projectId } = useRegistru();
  const { ruta } = useFir();
  const el = registru ? elementulRandului(registru.solutii, rand.id) : null;
  if (el?.fel !== "solutie" || !el.detaliu) return null;
  const mergi = (r: SolutieRand) => {
    if (r.id !== rand.id && potPleca()) navigate(caleAceleiasiPagini(projectId, ruta, r));
  };
  return (
    <span className="seg lant-faza" role="group" aria-label={t("lant.faza_aria")}>
      <button type="button" className={rand.faza === "macro" ? "on" : ""}
              aria-pressed={rand.faza === "macro"}
              onClick={() => mergi(el.macro)}>{t("faza.macro_scurt")}</button>
      <button type="button" className={rand.faza === "detaliu" ? "on" : ""}
              aria-pressed={rand.faza === "detaliu"}
              onClick={() => el.detaliu && mergi(el.detaliu)}>{t("faza.detaliu_scurt")}</button>
    </span>
  );
}

function Anunturi({ onDeschide }: { onDeschide: (href: string) => void }) {
  const t = useT();
  const { anunturi, inchideAnunt } = useRegistru();
  if (!anunturi.length) return null;
  return (
    <div className="nav-anunturi" role="status">
      {anunturi.map((a) => (
        <div key={a.id} className="card nav-anunt">
          <span>{a.text}</span>
          <button type="button" className="btn btnp"
                  onClick={() => { inchideAnunt(a.id); onDeschide(a.href); }}>
            {t("anunt.deschide")}
          </button>
          <button type="button" className="btn btng" aria-label={t("anunt.inchide")}
                  onClick={() => inchideAnunt(a.id)}>×</button>
        </div>
      ))}
    </div>
  );
}
